import type { Subscription, User } from '@/types/schemas/api'

/**
 * The single source of truth for subscription-based gating. Derived purely from Stripe
 * subscription status (mirrored into DynamoDB) — there is no separate flag to drift.
 *
 * - `active`    — active/trialing: full app access.
 * - `grace`     — past_due but < GRACE_HOURS old: full app access, but the account page
 *                 shows an "action required" nudge (covers an automatic-retry / bank lag).
 * - `needs_fix` — past_due past the grace window, or unpaid/incomplete: the subscription
 *                 still EXISTS, so send them to /account to fix payment — NOT to /billing.
 * - `none`      — canceled/incomplete_expired or no subscription at all: truly dead, so
 *                 send them to /billing to start a new subscription.
 */
export type SubscriptionState = 'active' | 'grace' | 'needs_fix' | 'none'

export const GRACE_HOURS = 24
const GRACE_SECONDS = GRACE_HOURS * 60 * 60

const ACTIVE = new Set(['active', 'trialing'])
// A failed renewal moves the sub into its new period and marks it past_due, so
// currentPeriodStart ≈ the moment it went past_due — the anchor for the grace window.
const FIXABLE = new Set(['unpaid', 'incomplete'])

const status = (s: Subscription) => s.subscriptionStatus ?? ''

export function subscriptionState(
  user: Pick<User, 'subscriptions'> | null | undefined,
  nowSeconds: number = Date.now() / 1000,
): SubscriptionState {
  const subs = user?.subscriptions ?? []
  if (subs.some((s) => ACTIVE.has(status(s)))) return 'active'

  const pastDue = subs.find((s) => status(s) === 'past_due')
  if (pastDue) {
    const since = Number(pastDue.currentPeriodStart ?? 0)
    return since && nowSeconds - since < GRACE_SECONDS ? 'grace' : 'needs_fix'
  }

  if (subs.some((s) => FIXABLE.has(status(s)))) return 'needs_fix'
  return 'none'
}

/** The plan picker: for a user with no subscription at all (no access to the app). */
export const UNSUBSCRIBED_BILLING_PATH = '/billing'
/** The account billing page: for a user who already has a subscription, active or trialing,
 *  whatever brings them there - a failed payment, a manage/upgrade request, an upgrade pitch.
 *  `/billing` sits behind BillingGuard and bounces a subscribed user straight back. */
export const SUBSCRIBED_BILLING_PATH = '/account/billing'
